import React, { useRef, useState } from "react";
import "./Contact.css";
import { MdOutlineEmail, MdContentCopy, MdCheck } from "react-icons/md";
import { BsLinkedin } from "react-icons/bs";
import { SiUpwork } from "react-icons/si";
import { FaArrowRight } from "react-icons/fa";
import Reveal from "../motion/Reveal";

const EMAIL = "wafabenmiloud@gmail.com";

// The message is written as one sentence; these are the blanks the visitor fills by picking a chip.
const TOPICS = ["a web platform", "an AI feature", "a job opportunity", "something else"];
const TIMINGS = ["as soon as possible", "in the next few months", "whenever the time is right"];

/** A row of chips that behaves like a radio group. */
const Choice = ({ label, options, value, onChange }) => (
  <span className="brief__choices" role="radiogroup" aria-label={label}>
    {options.map((option) => (
      <button
        key={option}
        type="button"
        role="radio"
        aria-checked={value === option}
        className={`brief__chip${value === option ? " is-active" : ""}`}
        onClick={() => onChange(option)}
      >
        {option}
      </button>
    ))}
  </span>
);

const Contact = () => {
  const [name, setName] = useState("");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [timing, setTiming] = useState(TIMINGS[0]);
  const [details, setDetails] = useState("");
  const [nameMissing, setNameMissing] = useState(false);
  const [copied, setCopied] = useState("");
  const nameRef = useRef(null);

  const message = [
    `Hi Wafa, my name is ${name.trim()}.`,
    `I'd like to talk about ${topic}, ideally ${timing}.`,
    details.trim(),
  ]
    .filter(Boolean)
    .join("\n\n");

  // The message can't go out unsigned: send the visitor back to the name blank.
  const requireName = () => {
    if (name.trim()) return true;
    setNameMissing(true);
    nameRef.current?.focus();
    return false;
  };

  const sendEmail = (e) => {
    e.preventDefault();
    if (!requireName()) return;
    const subject = `${topic[0].toUpperCase()}${topic.slice(1)} — from ${name.trim()}`;
    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Clipboard API unavailable or refused (older browsers, non-secure origins): copy via a selection.
      const helper = document.createElement("textarea");
      helper.value = text;
      helper.setAttribute("readonly", "");
      helper.style.position = "fixed";
      helper.style.opacity = "0";
      document.body.appendChild(helper);
      helper.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(helper);
      if (!ok) return;
    }
    setCopied(what);
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <Reveal as="section" id="contact">
      <h5 className="title">Get In Touch</h5>
      <h2>Contact Me</h2>

      <div className="container">
        <form className="brief" onSubmit={sendEmail} noValidate>
          <span className="brief__eyebrow">Finish the sentence — I'll take it from there</span>

          <div className="brief__sentence">
            <span className="brief__line">
              Hi Wafa 👋 my name is{" "}
              <input
                ref={nameRef}
                className={`brief__blank${nameMissing ? " is-missing" : ""}`}
                type="text"
                name="name"
                autoComplete="name"
                placeholder="your name"
                aria-label="Your name"
                aria-invalid={nameMissing}
                value={name}
                size={Math.max(name.length, 9)}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameMissing(false);
                }}
              />
              .
            </span>

            <span className="brief__line">
              I'd like to talk about
              <Choice label="What it is about" options={TOPICS} value={topic} onChange={setTopic} />
            </span>

            <span className="brief__line">
              ideally
              <Choice label="Timing" options={TIMINGS} value={timing} onChange={setTiming} />
            </span>
          </div>

          <label className="brief__details">
            <span>Anything else I should know? <em>(optional)</em></span>
            <textarea
              name="details"
              rows={3}
              placeholder="A link, a deadline, the stack you use, the role you're hiring for…"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
          </label>

          {nameMissing && (
            <span className="brief__error" role="alert">Add your name so I know who's writing.</span>
          )}

          <div className="brief__actions">
            <button type="submit" className="btn brief__send">
              Send it by email <FaArrowRight size={11} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="brief__ghost"
              onClick={() => requireName() && copy(message, "message")}
            >
              {copied === "message" ? <MdCheck aria-hidden="true" /> : <MdContentCopy aria-hidden="true" />}
              {copied === "message" ? "Copied" : "Copy the message"}
            </button>
          </div>
        </form>

        <div className="brief__channels">
          <span className="brief__channels-label">Prefer another way?</span>
          <button type="button" className="brief__channel" onClick={() => copy(EMAIL, "email")}>
            <MdOutlineEmail aria-hidden="true" />
            {copied === "email" ? "Address copied" : EMAIL}
          </button>
          <a
            className="brief__channel"
            href="https://www.linkedin.com/in/wafa-ben-miloud-382126181/"
            target="_blank"
            rel="noreferrer"
          >
            <BsLinkedin aria-hidden="true" /> LinkedIn
          </a>
          <a
            className="brief__channel"
            href="https://www.upwork.com/freelancers/~01fc5f7163d2b11b28?viewMode=1"
            target="_blank"
            rel="noreferrer"
          >
            <SiUpwork aria-hidden="true" /> Upwork
          </a>
        </div>
      </div>
    </Reveal>
  );
};

export default Contact;
